"use client";

import React, { useEffect, useState } from "react";
import { StarIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";

const API_URL = REST_API_URL;

export default function NetPromoterScore() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rating, setRating] = useState(0);
  const [reviews, setReviews] = useState(0);
  const [distribution, setDistribution] = useState({
    5: 0,
    4: 0,
    3: 0,
    2: 0,
    1: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!token) navigate("/sign-in");
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    const fetchReviewStats = async () => {
      try {
        setLoading(true);
        setError(null);

        const headers = buildHeaders();

        const res = await fetch(`${API_URL}/analytics/review-stats/`, {
          method: "GET",
          headers,
        });

        if (res.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          navigate("/sign-in");
          return;
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        setRating(parseFloat(data.average_rating) || 0);
        setReviews(data.total_reviews || 0);
        setDistribution({
          5: data.five_star_count || 0,
          4: data.four_star_count || 0,
          3: data.three_star_count || 0,
          2: data.two_star_count || 0,
          1: data.one_star_count || 0,
        });
      } catch (err) {
        console.error("❌ Failed to load review stats:", err);
        setError(t("home.errorReviewStats"));
      } finally {
        setLoading(false);
      }
    };

    fetchReviewStats();
  }, [token, navigate, t]);

  const total = Object.values(distribution).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="flex flex-col justify-center items-start p-6 bg-white rounded-2xl shadow-md w-full h-[343px]">
      <h2 className="text-[#302B2B] font-bold text-2xl">
        {t("home.netPromoterScoreTitle")}
      </h2>

      {loading ? (
        <p className="text-neutral-400 mt-4">{t("home.loadingReviewStats")}</p>
      ) : error ? (
        <p className="text-red-500 text-sm italic">{error}</p>
      ) : (
        <>
          <div className="flex flex-col mb-6">
            <p className="text-[40px] font-bold text-[#302B2B] leading-[49px]">
              {rating.toFixed(1)}
            </p>
            <p className="text-[#9A99AB] text-sm">
              {t("home.reviewsCount", { count: reviews })}
            </p>
          </div>

          <div className="flex flex-col w-full gap-2">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = distribution[star] || 0;
              const percent = ((count / total) * 100).toFixed(1);

              return (
                <div key={star} className="flex items-center gap-3 w-full">
                  <div className="relative flex items-center justify-center w-[34px] h-[34px]">
                    <StarIcon className="w-[28px] h-[28px] text-[#020637]" />
                    <span className="absolute text-white text-sm font-bold">
                      {star}
                    </span>
                  </div>

                  <div className="flex-1 h-[12px] bg-[#F6F6F6] rounded-full relative overflow-hidden">
                    <div
                      className="absolute top-0 left-0 h-full bg-[#030735] rounded-full transition-all duration-700"
                      style={{ width: `${percent}%` }}
                    />
                  </div>

                  <span className="text-[#9A99AB] text-sm w-[40px] text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
